import type { EmojiCustom, Pagination } from "sdk";
import { createResource, type Resource } from "solid-js";
import { logger } from "@/utils/logger";
import { PaginatedList } from "../core/PaginatedList";
import { BaseService } from "../core/Service";

const log = logger.for("api/emoji");

export class EmojiService extends BaseService<EmojiCustom> {
	protected cacheName = "emoji";

	private _roomLists = new Map<string, PaginatedList>();
	private _fetchedRooms = new Set<string>();

	getKey(item: EmojiCustom): string {
		return item.id;
	}

	async fetch(_id: string): Promise<EmojiCustom> {
		throw new Error("Use fetchByRoom(room_id, emoji_id) instead");
	}

	async fetchByRoom(room_id: string, emoji_id: string): Promise<EmojiCustom> {
		const data = await this.retryWithBackoff<EmojiCustom>(() =>
			this.client.http.GET("/api/v1/room/{room_id}/emoji/{emoji_id}", {
				params: { path: { room_id, emoji_id } },
			}),
		);
		this.upsert(data);
		return data;
	}

	private async fetchRoomPage(
		room_id: string,
		list: PaginatedList,
		cursor?: string,
	): Promise<void> {
		if (list.state.isLoading || !list.state.has_more) return;
		list.setLoading(true);

		try {
			const data = await this.retryWithBackoff<Pagination<EmojiCustom>>(() =>
				this.client.http.GET("/api/v1/room/{room_id}/emoji", {
					params: {
						path: { room_id },
						query: {
							dir: "f",
							limit: 1024,
							from: cursor,
						},
					},
				}),
			);

			this.upsertBulk(data.items);

			const newIds = data.items.map((emoji) => emoji.id);
			list.appendPage(newIds, data.has_more, data.items.at(-1)?.id);
		} catch (e) {
			log.error(String(e));
			list.setError(e);
			throw e;
		}
	}

	useRoomList(
		room_id: () => string | undefined,
	): Resource<PaginatedList | undefined> {
		const [resource] = createResource(room_id, async (rid) => {
			if (!rid) return undefined;

			let list = this._roomLists.get(rid);
			if (!list) {
				list = new PaginatedList();
				this._roomLists.set(rid, list);
			}

			if (list.state.ids.length === 0 && !list.state.isLoading) {
				await this.fetchRoomPage(rid, list);
			}

			return list;
		});

		return resource;
	}

	async search(query: string): Promise<Pagination<EmojiCustom>> {
		return await this.retryWithBackoff<Pagination<EmojiCustom>>(() =>
			this.client.http.GET("/api/v1/emoji/search", {
				params: {
					query: { query, limit: 100 },
				},
			}),
		);
	}

	async listAllCustom(roomIds: string[]): Promise<EmojiCustom[]> {
		const roomsToFetch = roomIds.filter((id) => !this._fetchedRooms.has(id));

		if (roomsToFetch.length === 0) {
			return [...this.cache.values()];
		}

		await Promise.all(
			roomsToFetch.map(async (room_id) => {
				try {
					const data = await this.retryWithBackoff<Pagination<EmojiCustom>>(
						() =>
							this.client.http.GET("/api/v1/room/{room_id}/emoji", {
								params: {
									path: { room_id },
									query: { limit: 1024 },
								},
							}),
					);
					this.upsertBulk(data.items);
					this._fetchedRooms.add(room_id);
				} catch (e) {
					log.error(String(e));
				}
			}),
		);
		return [...this.cache.values()];
	}

	clear() {
		super.clear();
		for (const list of this._roomLists.values()) {
			list.clear();
		}
		this._roomLists.clear();
		this._fetchedRooms.clear();
	}
}
